import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import VaultLogo from "@/components/VaultLogo";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", email: "", error: "" });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/api/notifications/unsubscribe/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setState({ status: "ok", email: data.email || "", error: "" });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          email: "",
          error: err?.response?.data?.detail || "Couldn't process that link.",
        });
      });
    return () => { cancelled = true; };
    // token is the only reactive dep; axios + API_BASE are module-level stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen noise flex flex-col">
      <header className="border-b-[1.5px] border-[var(--brand-ink)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="unsub-brand">
            <VaultLogo size={28}/>
            <span className="font-display text-2xl font-semibold tracking-tight">JobVault</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="card-hard rounded-md bg-white p-10 max-w-lg w-full" data-testid="unsub-card">
          {state.status === "loading" && (
            <>
              <p className="label-tiny mb-2">/unsubscribe</p>
              <h1 className="font-display text-4xl font-semibold tracking-tight">processing…</h1>
              <p className="text-[var(--brand-muted)] mt-3 font-mono-d text-sm">turning off the noise.</p>
            </>
          )}

          {state.status === "ok" && (
            <>
              <p className="label-tiny mb-2 flex items-center gap-1.5">
                <CheckCircle size={12} weight="duotone"/> /unsubscribed
              </p>
              <h1 className="font-display text-4xl font-semibold tracking-tight" data-testid="unsub-success">
                you&apos;re out.
              </h1>
              <p className="text-[var(--brand-muted)] mt-4">
                we won&apos;t email <b className="font-mono-d text-sm">{state.email}</b> about document expiries anymore.
              </p>
              <p className="text-[var(--brand-muted)] mt-3 text-sm">
                changed your mind? log in and flip the switch back on from your profile — no hard feelings.
              </p>
              <Link to="/login" data-testid="unsub-login-link"
                className="mt-6 inline-block btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm">
                back to JobVault
              </Link>
            </>
          )}

          {state.status === "error" && (
            <>
              <p className="label-tiny mb-2 flex items-center gap-1.5">
                <Warning size={12} weight="duotone"/> /error
              </p>
              <h1 className="font-display text-4xl font-semibold tracking-tight" data-testid="unsub-error">
                couldn&apos;t do that.
              </h1>
              <p className="text-[var(--brand-muted)] mt-4 font-mono-d text-sm">{state.error}</p>
              <p className="text-[var(--brand-muted)] mt-3 text-sm">
                if you got this link from an old email, the format may have changed.
                log in and turn off reminders from your profile instead.
              </p>
              <Link to="/login" data-testid="unsub-login-fallback"
                className="mt-6 inline-block btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm">
                log in
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
